import { Alert, Field, Select, cx } from "./ui";
import { PARCEL_CATEGORIES, PARCEL_TYPES, restrictionMessage } from "@/lib/parcelCatalog";
import type { Parcel } from "@/lib/types";

const CATEGORY_OPTIONS = [
  { value: "", label: "Choose the closest match" },
  ...PARCEL_CATEGORIES.map((category) => ({
    value: category.id,
    label: category.level === "none" ? category.label : `${category.label} -- ${levelLabel(category.level)}`,
  })),
];

function levelLabel(level: string) {
  if (level === "prohibited") return "prohibited";
  if (level === "dangerous") return "dangerous goods";
  return "restricted";
}

/**
 * One parcel's type + contents + dimensions, shared between the merchant
 * booking flow and the guest booking flow so the courier's real size/weight
 * tiers and prohibited-items list only live in one place.
 */
export function ParcelFields({
  parcel,
  categoryId,
  onChangeParcel,
  onChangeCategoryId,
  errors = {},
  prefix,
}: {
  parcel: Parcel;
  categoryId: string | null;
  onChangeParcel: (next: Partial<Parcel>) => void;
  onChangeCategoryId: (next: string | null) => void;
  errors?: Record<string, string>;
  prefix: string;
}) {
  const activeType = PARCEL_TYPES.find(
    (type) =>
      type.preset.length_mm === parcel.length_mm &&
      type.preset.width_mm === parcel.width_mm &&
      type.preset.height_mm === parcel.height_mm &&
      type.preset.weight_g === parcel.weight_g,
  );
  const category = PARCEL_CATEGORIES.find((c) => c.id === categoryId);
  const message = category ? restrictionMessage(category.level) : null;

  return (
    <div className="space-y-4">
      <div>
        <p className="ws-label">Parcel type</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {PARCEL_TYPES.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => onChangeParcel({ ...type.preset })}
              className={cx(
                "rounded-xl border p-3 text-left text-sm transition",
                activeType?.id === type.id
                  ? "border-brand bg-brand-soft text-brand"
                  : "border-ink-200 hover:border-ink-300",
              )}
            >
              <p className="font-medium">{type.label}</p>
              <p className="mt-0.5 text-xs text-ink-400">{type.hint}</p>
            </button>
          ))}
        </div>
      </div>

      <Select
        label="What's inside?"
        name={`${prefix}-category`}
        value={categoryId ?? ""}
        onChange={(e) => onChangeCategoryId(e.target.value || null)}
        options={CATEGORY_OPTIONS}
      />
      {message && <Alert tone={category?.level === "restricted" ? "warn" : "bad"}>{message}</Alert>}
      {categoryId === "other" && (
        <Field
          label="Describe what's inside"
          value={parcel.description ?? ""}
          onChange={(e) => onChangeParcel({ description: e.target.value })}
          placeholder="Cake boxes, documents, spare parts"
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field
          label="Length (mm)"
          type="number"
          min={1}
          value={parcel.length_mm}
          onChange={(e) => onChangeParcel({ length_mm: Number(e.target.value) })}
          error={errors[`${prefix}.length_mm`]}
        />
        <Field
          label="Width (mm)"
          type="number"
          min={1}
          value={parcel.width_mm}
          onChange={(e) => onChangeParcel({ width_mm: Number(e.target.value) })}
          error={errors[`${prefix}.width_mm`]}
        />
        <Field
          label="Height (mm)"
          type="number"
          min={1}
          value={parcel.height_mm}
          onChange={(e) => onChangeParcel({ height_mm: Number(e.target.value) })}
          error={errors[`${prefix}.height_mm`]}
        />
        <Field
          label="Weight (g)"
          type="number"
          min={1}
          value={parcel.weight_g}
          onChange={(e) => onChangeParcel({ weight_g: Number(e.target.value) })}
          error={errors[`${prefix}.weight_g`]}
          hint="Carriers charge on the greater of actual and volumetric weight."
        />
      </div>
    </div>
  );
}

export function isProhibited(categoryId: string | null): boolean {
  return PARCEL_CATEGORIES.find((c) => c.id === categoryId)?.level === "prohibited";
}

export function describedParcel(parcel: Parcel, categoryId: string | null): Parcel {
  const category = PARCEL_CATEGORIES.find((c) => c.id === categoryId);
  if (!category || category.id === "other") return parcel;
  return { ...parcel, description: category.label };
}
